import React from 'react';
import { GENERATION_EMISSIONS_MODEL_SCOPE_NOTE, GENERATION_WATER_MODEL_SCOPE_NOTE } from '../../../lib/generationImpact/reportProjection';

import type { SiteRagReport } from '../../../types/siteRag';
import type {
  EngineeringVerdict,
  GenerationCapacityScreenVerdict,
  GenerationImpactReportSource,
} from '../../../types/generationImpact';
import { useEffectiveReportTheme } from '../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../lib/reportRoleColors';
import { EditorialBadge, EditorialCallout, EditorialDataTable, EditorialSubsection } from './editorial';
import { ReportSourceNote } from './ReportSourceNote';

const fmt = (value: number | null, digits = 1): string =>
  value == null ? 'Indeterminate' : value.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const badgeTone = (verdict: EngineeringVerdict): 'positive' | 'negative' | 'neutral' =>
  verdict === 'pass' ? 'positive' : verdict === 'fail' ? 'negative' : 'neutral';

const calloutTone = (verdict: EngineeringVerdict): 'positive' | 'negative' | 'accent' =>
  verdict === 'pass' ? 'positive' : verdict === 'fail' ? 'negative' : 'accent';

const verdictLabel = (verdict: EngineeringVerdict): string =>
  verdict === 'pass' ? 'Pass' : verdict === 'fail' ? 'Shortfall' : 'Indeterminate';

/** Joint-screen tones: 'conditional' is a caution, never a green pass. */
const screenCalloutTone = (verdict: GenerationCapacityScreenVerdict): 'positive' | 'negative' | 'accent' =>
  verdict === 'pass' ? 'positive' : verdict === 'fail' ? 'negative' : 'accent';

const screenCalloutLabel = (verdict: GenerationCapacityScreenVerdict): string =>
  verdict === 'pass'
    ? 'Capacity screen · pass'
    : verdict === 'conditional'
      ? 'Capacity screen · conditional'
      : verdict === 'fail'
        ? 'Capacity screen · shortfall'
        : 'Capacity screen · indeterminate';

const SourceLine: React.FC<{ source: GenerationImpactReportSource }> = ({ source }) => {
  return (
    <ReportSourceNote
      variant="label"
      className="mb-3"
      testId="generation-impact-report-source"
    >
      Promoted scenario · {source.scenarioName} · {source.modelVersion} · {source.inputHash.slice(0, 8)} / {source.resultHash.slice(0, 8)}
      {source.layoutVersion != null ? ` · published layout v${source.layoutVersion}` : ''}
      {source.parcelBoundaryHash
        ? ` · frozen parcel ${source.parcelBoundaryAreaAcres?.toFixed(2) ?? 'indeterminate'} ac · ${source.parcelBoundarySource ?? 'unknown source'} · ${source.parcelBoundaryHash.slice(0, 8)}`
        : ''}
    </ReportSourceNote>
  );
};

export const GenerationCapacityResilienceBlock: React.FC<{ report: SiteRagReport }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const projection = report.generationImpactProjection;
  const data = projection?.capacityAndResilience;
  if (!data || !projection) return null;
  return (
    <EditorialSubsection theme={theme} label="Promoted scenario · Capacity & resilience" accentLabel>
      <SourceLine source={projection.source} />
      <EditorialDataTable
        theme={theme}
        data-testid="generation-impact-capacity-projection"
        columns={[{ header: 'Duty point' }, { header: 'MW', align: 'right', numeric: true }, { header: 'Status', align: 'right' }]}
        rows={[
          [data.capacityRole === 'grid-supply' ? 'Aggregate grid-feed capacity' : 'Normal net output', fmt(data.normalNetMW), 'Operating basis'],
          ['Facility load', fmt(data.facilityLoadMW), 'User basis'],
          ['Critical load', fmt(data.criticalLoadMW), 'User basis'],
          [`Reserve requirement (${fmt(data.reserveMarginPct)}%)`, fmt(data.reserveRequiredMW), 'User basis'],
          [`${data.criterion} ${data.capacityRole === 'grid-supply' ? 'modeled grid supply' : 'net'} after ${fmt(data.contingencyLostMW)} MW loss`, fmt(data.contingencyNetMW), verdictLabel(data.loadVerdict)],
          [`${data.criterion} headroom vs critical load`, fmt(data.loadMarginMW), <EditorialBadge key="load" theme={theme} tone={badgeTone(data.loadVerdict)}>{verdictLabel(data.loadVerdict)}</EditorialBadge>],
          [`${data.criterion} headroom vs reserve requirement`, fmt(data.reserveMarginMW), <EditorialBadge key="reserve" theme={theme} tone={badgeTone(data.reserveVerdict)}>{verdictLabel(data.reserveVerdict)}</EditorialBadge>],
        ]}
      />
      {data.screenVerdict && data.screenStatement && (
        <EditorialCallout
          theme={theme}
          tone={screenCalloutTone(data.screenVerdict)}
          label={screenCalloutLabel(data.screenVerdict)}
          id="generation-impact-capacity-screen-verdict"
        >
          {data.screenStatement}
          {data.screeningBasis
            ? ` Screening derate basis: ${data.screeningBasis.label}`
              + `${data.screeningBasis.uniform ? '' : ' (mixed across units)'}. Verdicts are basis-dependent; `
              + 'compare scenarios only on a common basis.'
            : ''}
        </EditorialCallout>
      )}
      <p className="text-xs" style={{ color: roleColor(theme, 'muted') }}>
        Probability load served: {data.probabilityLoadServed == null ? 'not calculated' : `${(data.probabilityLoadServed * 100).toFixed(2)}%`}. {data.reliabilityCaveat}
      </p>
      {data.qualificationNote && (
        <p className="text-xs" style={{ color: roleColor(theme, 'muted') }} data-testid="generation-impact-grid-supply-qualification">
          {data.qualificationNote}
        </p>
      )}
      {data.annualization && (
        <p className="text-xs" style={{ color: roleColor(theme, 'muted') }} data-testid="generation-impact-annualization-note">
          {data.annualization.note}
        </p>
      )}
      {data.reliabilityPerspective && (
        <p className="text-xs" style={{ color: roleColor(theme, 'muted') }} data-testid="generation-impact-reliability-perspective">
          {data.reliabilityPerspective.note}
        </p>
      )}
      {data.gasDemand && (
        <p className="text-xs" style={{ color: roleColor(theme, 'muted') }} data-testid="generation-impact-gas-demand-perspective">
          {data.gasDemand.note}
        </p>
      )}
    </EditorialSubsection>
  );
};

/**
 * Indicative screening economics — the platform's answer to "what does it
 * cost", present only when a gen-time projection carried the basis (SED
 * fuel price + frozen scenario). Explicitly screening-grade; the note and
 * caption carry the caveats so silence is never the only cost story.
 */
export const GenerationScreeningEconomicsBlock: React.FC<{ report: SiteRagReport }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const projection = report.generationImpactProjection;
  const data = projection?.screeningEconomics;
  if (!data || !projection) return null;
  const cfLabel = (cf: number | null): string => (cf == null ? 'n/a' : `${Math.round(cf * 100)}% CF`);
  const range = (low: number | null, high: number | null): string =>
    low == null || high == null ? 'Indeterminate' : `$${low.toFixed(0)}-$${high.toFixed(0)}`;
  return (
    <EditorialSubsection theme={theme} label="Promoted scenario · Screening economics (indicative)" accentLabel>
      <SourceLine source={projection.source} />
      <EditorialDataTable
        theme={theme}
        data-testid="generation-impact-screening-economics"
        columns={[
          { header: 'Installed-cost class' },
          { header: `$/MWh at ${cfLabel(data.servedCapacityFactor)} (served)`, align: 'right' },
          { header: `$/MWh at ${cfLabel(data.capabilityCapacityFactor)} (capability)`, align: 'right' },
        ]}
        rows={data.rows.map((row) => [
          `${row.label} ($${row.lowUsdPerKw}-$${row.highUsdPerKw}/kW)`,
          range(row.servedUsdPerMwhLow, row.servedUsdPerMwhHigh),
          range(row.capabilityUsdPerMwhLow, row.capabilityUsdPerMwhHigh),
        ])}
      />
      {data.capture && (
        <p className="text-xs" style={{ color: roleColor(theme, 'muted') }} data-testid="generation-impact-45q-note">
          45Q at ${data.capture.creditUsdPerTonne}/t on {data.capture.servedCaptureTonnesPerYear == null
            ? 'an indeterminate served capture volume'
            : `~${(data.capture.servedCaptureTonnesPerYear / 1e6).toFixed(1)} Mt/yr served capture`}
          {data.capture.servedCreditMUsdPerYear == null ? '' : ` (~$${data.capture.servedCreditMUsdPerYear.toFixed(0)}M/yr)`}
          {' '}vs a ${data.capture.captureCostUsdPerTonneLow}-${data.capture.captureCostUsdPerTonneHigh}/t capture-cost
          archetype. {data.capture.note}
        </p>
      )}
      <p className="text-xs" style={{ color: roleColor(theme, 'muted') }}>
        Fuel ${data.fuelUsdPerMwhNet.toFixed(1)}/MWh net at ${data.fuelPriceUsdPerMmbtu.toFixed(2)}/MMBtu
        ({data.fuelPriceSource}) · O&amp;M ${data.omUsdPerMwh}/MWh · FCR {Math.round(data.fixedChargeRate * 100)}%.
        {' '}{data.note}
      </p>
    </EditorialSubsection>
  );
};

/**
 * Indicative screening economics — the platform's answer to "what does it
 * cost", present only when a gen-time projection carried the basis (SED
 * fuel price + frozen scenario). Explicitly screening-grade; the note and
 * caption carry the caveats so silence is never the only cost story.
 */
export const GenerationWaterBlock: React.FC<{ report: SiteRagReport }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const projection = report.generationImpactProjection;
  const data = projection?.waterResource;
  if (!data || !projection) return null;
  return (
    <EditorialSubsection theme={theme} label="Promoted scenario · Water duty" accentLabel>
      <SourceLine source={projection.source} />
      <EditorialDataTable
        theme={theme}
        data-testid="generation-impact-water-projection"
        columns={[{ header: 'Water duty' }, { header: 'gal/day', align: 'right', numeric: true }]}
        rows={[
          ['Generation withdrawal', fmt(data.generationWithdrawalGalPerDay, 0)],
          ['Generation consumption', fmt(data.generationConsumptionGalPerDay, 0)],
          ['CCS withdrawal', fmt(data.ccsWithdrawalGalPerDay, 0)],
          ['CCS consumption', fmt(data.ccsConsumptionGalPerDay, 0)],
          ['Data-center cooling withdrawal', fmt(data.dataCenterWithdrawalGalPerDay, 0)],
          ['Data-center cooling consumption', fmt(data.dataCenterConsumptionGalPerDay, 0)],
          ['Modeled component withdrawal subtotal', fmt(data.totalWithdrawalGalPerDay, 0)],
          ['Modeled component consumption subtotal', fmt(data.totalConsumptionGalPerDay, 0)],
          ['Declared consumptive-use capacity', fmt(data.availableConsumptionGalPerDay, 0)],
          ['Consumption headroom', fmt(data.consumptionHeadroomGalPerDay, 0)],
        ]}
      />
      <EditorialBadge theme={theme} tone={badgeTone(data.adequacy)}>{verdictLabel(data.adequacy)}</EditorialBadge>
      {data.basis && (
        <div data-testid="generation-impact-water-basis">
          <EditorialCallout theme={theme} tone="accent" label="Basis">
            {data.basis.statement}
          </EditorialCallout>
        </div>
      )}
      {!data.basis && <p className="text-xs" style={{ color: roleColor(theme, 'muted') }}>{GENERATION_WATER_MODEL_SCOPE_NOTE}</p>}
    </EditorialSubsection>
  );
};

export const GenerationFuelSupplyBlock: React.FC<{ report: SiteRagReport }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const projection = report.generationImpactProjection;
  const data = projection?.fuelSupply;
  if (!data || !projection) return null;
  const pressure = (value: number | null, reference: 'gauge' | 'absolute' | null): string =>
    value == null ? 'Not documented' : `${fmt(value, 0)} kPa ${reference ?? ''}`.trim();
  const binding = (limit: 'station-design' | 'contract-mdq' | null): string =>
    limit === 'contract-mdq' ? 'Contract binds' : limit === 'station-design' ? 'Station binds' : 'Indeterminate';
  return (
    <EditorialSubsection theme={theme} label="Promoted scenario · Fuel-gas interconnect" accentLabel>
      <SourceLine source={projection.source} />
      {/* The firm/indicative distinction leads: a scoping-class rating must
          never read as contracted transport. */}
      <EditorialBadge theme={theme} tone={data.quantityBasis === 'firm-transport-agreement' ? 'positive' : 'neutral'}>
        {data.quantityBasis === 'firm-transport-agreement'
          ? 'Firm transport agreement'
          : 'Indicative · scoping class, not firm transport'}
      </EditorialBadge>
      <EditorialDataTable
        theme={theme}
        data-testid="generation-impact-fuel-supply-projection"
        columns={[{ header: 'Fuel-gas interconnect' }, { header: 'Dth/day', align: 'right', numeric: true }]}
        rows={[
          ['Calculated peak nomination', fmt(data.peakGasDthPerDay, 0)],
          ['Station design capacity', fmt(data.stationDthPerDay, 0)],
          ['Contract maximum daily quantity', fmt(data.contractDthPerDay, 0)],
          [`Deliverable at rated pressure (${binding(data.firmBindingLimit)})`, fmt(data.firmDeliverableDthPerDay, 0)],
          ['Headroom at rated pressure', fmt(data.headroomAtRatedPressureDthPerDay, 0)],
          [`Deliverable at minimum delivery pressure (${binding(data.deratedBindingLimit)})`, fmt(data.deratedDeliverableDthPerDay, 0)],
          ['Headroom at minimum delivery pressure', fmt(data.headroomAtMinimumPressureDthPerDay, 0)],
        ]}
      />
      <EditorialDataTable
        theme={theme}
        data-testid="generation-impact-fuel-supply-pressure"
        columns={[{ header: 'Pressure basis' }, { header: 'Value', align: 'right' }]}
        rows={[
          ['Capacity rated at', pressure(data.ratedAtPressureKPa, data.ratedAtPressureReference)],
          ['Guaranteed minimum delivery pressure', pressure(data.documentedMinimumDeliveryPressureKPa, data.documentedMinimumDeliveryPressureReference)],
          ['Derate factor applied', data.absolutePressureRatio == null ? 'Not established' : `${fmt(data.absolutePressureRatio * 100, 1)}%`],
          ['Compressibility basis', data.compressibilityBasis === 'documented' ? 'Documented real-gas Z' : `Ideal gas · ±${fmt(data.uncertaintyDthPerDay, 0)} Dth/day band`],
        ]}
      />
      <EditorialBadge theme={theme} tone={badgeTone(data.adequacy)}>{verdictLabel(data.adequacy)}</EditorialBadge>
      {data.assumptions.length > 0 && (
        <EditorialCallout theme={theme} tone={calloutTone(data.adequacy)} label="Screening assumptions">
          {data.assumptions.join(' ')}
        </EditorialCallout>
      )}
    </EditorialSubsection>
  );
};

export const GenerationFootprintBlock: React.FC<{ report: SiteRagReport }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const projection = report.generationImpactProjection;
  const data = projection?.testFitFootprint;
  if (!data || !projection) return null;
  return (
    <EditorialSubsection theme={theme} label="Promoted scenario · Generation footprint" accentLabel>
      <SourceLine source={projection.source} />
      <EditorialDataTable
        theme={theme}
        data-testid="generation-impact-footprint-projection"
        columns={[{ header: 'Planning block' }, { header: 'Acres', align: 'right', numeric: true }]}
        rows={[
          ['Generation plant', fmt(data.plantAcres, 2)],
          ['Cooling', fmt(data.coolingAcres, 2)],
          ['Switchyard', fmt(data.switchyardAcres, 2)],
          ['CCS', fmt(data.ccsAcres, 2)],
          ['Compression', fmt(data.compressionAcres, 2)],
          ['Total promoted footprint', <strong key="total">{fmt(data.totalAcres, 2)}</strong>],
          ['Gross parcel / boundary', fmt(data.parcelAreaAcres ?? null, 2)],
          ['Wetlands deduction', fmt(data.wetlandAcres ?? null, 2)],
          ['Flood-overlap deduction', fmt(data.floodOverlapAcres ?? null, 2)],
          ['Setback planning allowance', fmt(data.setbackAllowanceAcres ?? null, 2)],
          ['Net-buildable planning screen', fmt(data.netBuildableAcres ?? null, 2)],
          ['Generation-footprint margin', fmt(data.footprintMarginAcres ?? null, 2)],
          ['Unique acreage represented by published polygons', fmt(data.representedFootprintAcres ?? null, 2)],
          ['Overlapped / unrepresented footprint', fmt(data.overlappingFootprintAcres ?? null, 2)],
          ['Published geometry outside parcel', fmt(data.outsideBoundaryAcres ?? null, 2)],
        ]}
      />
      <div className="flex flex-wrap gap-2">
        <EditorialBadge theme={theme} tone={badgeTone(data.acreageFitVerdict ?? 'indeterminate')}>
          Generation acreage: {verdictLabel(data.acreageFitVerdict ?? 'indeterminate')}
        </EditorialBadge>
        <EditorialBadge theme={theme} tone={badgeTone(data.spatialFitVerdict ?? 'indeterminate')}>
          Published geometry: {verdictLabel(data.spatialFitVerdict ?? 'indeterminate')}
        </EditorialBadge>
      </div>
      {(data.netBuildableBasis === 'partial-planning-screen' || data.netBuildableBasis === 'unavailable' || data.constraintDataComplete === false) && (
        <EditorialCallout
          theme={theme}
          tone="accent"
          label="Net-buildable basis"
          id="generation-impact-net-buildable-basis-note"
        >
          {data.netBuildableBasis === 'unavailable'
            ? 'Net-buildable acreage is not established for this scope; the deductions shown are partial inputs, not a usable-land figure.'
            : `Partial planning screen: at least one constraint input is incomplete (for example flood geometry computed on a disclosed fallback after an exact-precision clipping failure, or a screening that is not complete). The deductions shown are conservative inputs and the generation acreage verdict ${data.acreageFitVerdict === 'fail' ? 'is a shortfall measured against that incomplete basis' : 'is indeterminate, not a pass'}.`}
        </EditorialCallout>
      )}
      <EditorialCallout
        theme={theme}
        tone={calloutTone(data.spatialFitVerdict ?? 'indeterminate')}
        label="Generation-layout-only scope"
      >
        {data.spatialFitReason ?? 'Geometric containment was not assessed. This is not an overall campus or data-hall fit verdict.'}
      </EditorialCallout>
      {data.footprintConventionNote && (
        <EditorialCallout
          theme={theme}
          tone="accent"
          label="Footprint basis divergence"
          id="generation-impact-footprint-convention-note"
        >
          {data.footprintConventionNote}
        </EditorialCallout>
      )}
    </EditorialSubsection>
  );
};

export const GenerationEmissionsBlock: React.FC<{ report: SiteRagReport }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const projection = report.generationImpactProjection;
  const data = projection?.environmentalAndCommunity;
  if (!data || !projection) return null;
  return (
    <EditorialSubsection theme={theme} label="Promoted scenario · Operating emissions" accentLabel>
      <SourceLine source={projection.source} />
      <EditorialDataTable
        theme={theme}
        data-testid="generation-impact-emissions-projection"
        columns={[{ header: 'Annual operating emission' }, { header: 'tonnes/year', align: 'right', numeric: true }]}
        rows={[
          ['Modeled on-site generation uncontrolled CO2', fmt(data.uncontrolledCo2TonnesPerYear, 0)],
          ['Modeled on-site generation captured CO2', fmt(data.capturedCo2TonnesPerYear, 0)],
          ['Modeled on-site generation residual CO2', fmt(data.residualCo2TonnesPerYear, 0)],
          ['NOx', fmt(data.noxTonnesPerYear, 1)],
          ['CO', fmt(data.coTonnesPerYear, 1)],
        ]}
      />
      <p className="text-xs" style={{ color: roleColor(theme, 'muted') }}>{GENERATION_EMISSIONS_MODEL_SCOPE_NOTE}</p>
      {data.annualization && (
        <p className="text-xs" style={{ color: roleColor(theme, 'muted') }} data-testid="generation-impact-emissions-annualization-note">
          {data.annualization.note}
        </p>
      )}
    </EditorialSubsection>
  );
};

export const GenerationNoiseLightingBlock: React.FC<{ report: SiteRagReport }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const projection = report.generationImpactProjection;
  const data = projection?.environmentalAndCommunity;
  if (!data || !projection) return null;
  const rows = data.noise.map((row) => [
    `${row.label}${row.distanceM == null ? '' : ` · ${fmt(row.distanceM, 0)} m`}`,
    row.predictedDbA == null ? 'Indeterminate' : `${row.predictedDbA.toFixed(1)} dBA`,
    row.thresholdDbA == null ? 'No threshold' : `${row.thresholdDbA.toFixed(1)} dBA`,
    <EditorialBadge key={row.receptorId} theme={theme} tone={badgeTone(row.verdict)}>{verdictLabel(row.verdict)}</EditorialBadge>,
  ]);
  const caveats = [...new Set(data.noise.map((row) => row.caveat).filter(Boolean))];
  return (
    <EditorialSubsection theme={theme} label="Promoted scenario · Noise & lighting screen" accentLabel>
      <SourceLine source={projection.source} />
      {rows.length > 0 ? (
        <EditorialDataTable
          theme={theme}
          data-testid="generation-impact-noise-projection"
          columns={[{ header: 'Receptor' }, { header: 'Predicted', align: 'right' }, { header: 'Threshold', align: 'right' }, { header: 'Status', align: 'right' }]}
          rows={rows}
        />
      ) : <p className="text-xs">No receptor noise result was determined for the promoted scenario.</p>}
      {caveats.length > 0 && (
        <p className="mt-1 text-[11px]" style={{ color: roleColor(theme, 'muted') }}>
          Screening basis: {caveats.join(' ')}
        </p>
      )}
      <p className="text-xs" style={{ color: roleColor(theme, 'body') }}>
        Lighting load: {fmt(data.lighting.installedPowerKW, 1)} kW · installed output: {fmt(data.lighting.installedLumens, 0)} lumens.
      </p>
      <p className="mt-1 text-xs" style={{ color: roleColor(theme, 'muted') }}>
        Controls: fully shielded {data.lighting.fullyShielded == null ? 'not documented' : data.lighting.fullyShielded ? 'yes' : 'no'} · BUG-rated {data.lighting.bugRated == null ? 'not documented' : data.lighting.bugRated ? 'yes' : 'no'} · motion/curfew {data.lighting.motionOrCurfewControls == null ? 'not documented' : data.lighting.motionOrCurfewControls ? 'yes' : 'no'} · photometric file {data.lighting.iesPhotometricAvailable == null ? 'not documented' : data.lighting.iesPhotometricAvailable ? 'available' : 'missing'}.
      </p>
      {data.lighting.planningFlags.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs" style={{ color: roleColor(theme, 'muted') }}>
          {data.lighting.planningFlags.map((flag) => <li key={flag}>{flag}</li>)}
        </ul>
      )}
    </EditorialSubsection>
  );
};

/**
 * PROMOTED-VS-REJECTED screening comparison.
 *
 * Promotion is a funnel to exactly one authority, so without this a report can
 * only ever present the winner — as though nothing else was ever considered.
 * Every figure here is read from the losing scenarios' OWN frozen solver
 * results; nothing is recomputed at render time, and the block states in the
 * table itself that the rejected rows carry no authority.
 *
 * Renders nothing unless a real comparison (two or more configurations) was
 * recorded, so a single-scenario report is unchanged.
 */
export const GenerationComparisonBlock: React.FC<{ report: SiteRagReport }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const projection = report.generationImpactProjection;
  const data = projection?.comparison;
  if (!data || !projection || data.rows.length < 2) return null;
  return (
    <EditorialSubsection theme={theme} label="Configurations screened before promotion" accentLabel>
      <SourceLine source={projection.source} />
      <EditorialCallout theme={theme} tone="accent">{data.statement}</EditorialCallout>
      <EditorialDataTable
        theme={theme}
        data-testid="generation-impact-comparison-projection"
        columns={[
          { header: 'Configuration' },
          { header: 'Equipment basis' },
          { header: 'Criterion net (MW)', align: 'right', numeric: true },
          { header: 'Reserve margin (MW)', align: 'right', numeric: true },
          { header: 'Peak gas (MMBtu/h)', align: 'right', numeric: true },
          { header: 'Peak water (gal/day)', align: 'right', numeric: true },
          { header: 'Footprint (ac)', align: 'right', numeric: true },
        ]}
        rows={data.rows.map(row => [
          row.promoted
            ? <strong key={row.scenarioId}>{row.scenarioName} · promoted</strong>
            : <span key={row.scenarioId}>{row.scenarioName} · screened only</span>,
          row.equipmentBasis,
          `${row.criterionLabel} · ${fmt(row.criterionNetMW)}`,
          fmt(row.reserveMarginMW),
          fmt(row.peakGasMmbtuPerHour, 0),
          fmt(row.peakWaterConsumptionGalPerDay, 0),
          fmt(row.footprintTotalAcres, 1),
        ])}
      />
      <p className="mt-2 text-xs" style={{ color: roleColor(theme, 'muted') }}>
        Only the promoted configuration is a duty basis. The screened rows are recorded
        for method transparency and are not carried into any downstream calculation.
        {data.unresolvedScenarioIds.length > 0
          ? ` ${data.unresolvedScenarioIds.length} compared configuration${data.unresolvedScenarioIds.length === 1 ? '' : 's'} could not be read at generation time and ${data.unresolvedScenarioIds.length === 1 ? 'is' : 'are'} omitted.`
          : ''}
      </p>
    </EditorialSubsection>
  );
};

export const GenerationGoNoGoBlock: React.FC<{ report: SiteRagReport }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const projection = report.generationImpactProjection;
  const data = projection?.goNoGoSummary;
  if (!data || !projection) return null;
  return (
    <EditorialSubsection theme={theme} label="Promoted generation scenario · Capacity screen" accentLabel>
      <SourceLine source={projection.source} />
      <div data-testid="generation-impact-gonogo-projection">
        <EditorialCallout theme={theme} tone={calloutTone(data.capacityVerdict)}>{data.summary}</EditorialCallout>
      </div>
      {data.risks.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs" style={{ color: roleColor(theme, 'body') }}>
          {data.risks.map((risk) => <li key={risk.id}>{risk.message}</li>)}
        </ul>
      )}
      {data.gaps.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs" style={{ color: roleColor(theme, 'negative') }}>
          {data.gaps.map((gap) => <li key={gap}>Data gap: {gap}</li>)}
        </ul>
      )}
      {data.warnings.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs" style={{ color: roleColor(theme, 'muted') }}>
          {data.warnings.map((warning) => <li key={warning}>Planning warning: {warning}</li>)}
        </ul>
      )}
    </EditorialSubsection>
  );
};

/**
 * MAX-POWER ENVELOPE — the largest fleet the site's documented constraints allow,
 * and the campus it supports.
 *
 * Three things this block must never do, because each would turn a screening
 * figure into a false claim:
 *   - print the capacity without its `qualifier` ('≥' when nothing bound the sweep)
 *   - print it without the bound-quality sentence
 *   - drop the caveats, which name what the documented constraint set excludes
 */
export const GenerationMaxPowerBlock: React.FC<{ report: SiteRagReport }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const projection = report.generationImpactProjection;
  const data = projection?.maxPowerEnvelope;
  if (projection?.maxPowerWithheldReason) return (
    <EditorialSubsection theme={theme} label="Max-power study requires recomputation" accentLabel>
      <p>{projection.maxPowerWithheldReason}</p>
    </EditorialSubsection>
  );
  if (!data || !projection) return null;

  const q = data.qualifier ? `${data.qualifier} ` : '';
  const boundSentence =
    data.boundQuality === 'search-ceiling-reached'
      ? 'No documented constraint bound this site within the evaluated range, so this figure is a FLOOR — read it as "at least", never as a ceiling.'
      : data.boundQuality === 'upper-bound-undocumented-constraints'
        ? 'At least one constraint is undocumented, so this is an UPPER BOUND — the true maximum can only be lower.'
        : 'Bounded by the constraints documented for this site. The documented set is still incomplete; see the assumptions below.';

  return (
    <EditorialSubsection theme={theme} label="Promoted scenario · Max-power envelope" accentLabel>
      <SourceLine source={projection.source} />
      <div data-testid="generation-impact-maxpower-projection">
        <EditorialCallout theme={theme} tone="accent">{data.bindingStatement}</EditorialCallout>
      </div>
      <EditorialDataTable
        theme={theme}
        data-testid="generation-impact-maxpower-table"
        columns={[{ header: 'Envelope' }, { header: 'Value', align: 'right', numeric: true }, { header: 'Basis', align: 'right' }]}
        rows={[
          ['Maximum firm generation', `${q}${fmt(data.firmNetMW)} MW`, `N-k firm · ${data.blockCount} × ${fmt(data.isoGrossMWPerBlock)} MW`],
          ['Installed ISO nameplate', `${q}${fmt(data.isoGrossNameplateMW)} MW`, 'Before contingency'],
          ['Supportable facility load', `${q}${fmt(data.facilityMW)} MW`, `After ${data.tdLossPct}% T&D`],
          ['At generation terminals', `${q}${fmt(data.facilityMWAtGenerationTerminals)} MW`, 'Behind-the-meter reading'],
          ['Supportable IT load', `${q}${fmt(data.itLoadMW)} MW`, `PUE ${data.pue} (${data.pueSource})`],
          ['Apparent power', `${q}${fmt(data.apparentMVA)} MVA`, 'Transformer / switchgear basis'],
        ]}
      />
      <p className="mt-2 text-xs" style={{ color: roleColor(theme, 'muted') }} data-testid="generation-impact-maxpower-bound">
        {boundSentence}
      </p>
      <EditorialDataTable
        theme={theme}
        data-testid="generation-impact-maxpower-ceilings"
        columns={[{ header: 'Constraint' }, { header: 'Ceiling (site gross MW)', align: 'right', numeric: true }, { header: 'Status', align: 'right' }]}
        rows={data.ceilings.map((c) => [
          c.label,
          c.ceilingSiteGrossMW != null ? fmt(c.ceilingSiteGrossMW) : '—',
          c.status,
        ])}
      />
      {data.landFitNonMonotonic && (
        <p className="mt-2 text-xs" style={{ color: roleColor(theme, 'negative') }}>
          The parcel packer is not monotone in capacity; this is the largest verified fit on the evaluated
          ladder, not a supremum.
        </p>
      )}
      {data.caveats.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs" style={{ color: roleColor(theme, 'muted') }}>
          {data.caveats.map((c) => <li key={c}>{c}</li>)}
        </ul>
      )}
    </EditorialSubsection>
  );
};
